import React, { useEffect, useState } from "react"

type PageTransitionProps = {
  children: React.ReactNode
  /** Current location pathname, used to detect route changes */
  locationPathname: string
}

/**
 * Simple fade transition between Gatsby pages.
 *
 * On navigation:
 * - Fade out over 200ms
 * - Swap content
 * - Fade in over 200ms
 *
 * Total perceived transition duration ≈ 400ms.
 */
export const PageTransition: React.FC<PageTransitionProps> = ({
  children,
  locationPathname,
}) => {
  const [displayedChildren, setDisplayedChildren] = useState(children)
  const [stage, setStage] = useState<"fadeIn" | "fadeOut">("fadeIn")
  const [currentPathname, setCurrentPathname] = useState(locationPathname)

  useEffect(() => {
    if (locationPathname === currentPathname) return

    // Start fade-out of current page
    setStage("fadeOut")

    const timeout = window.setTimeout(() => {
      // After fade-out, swap content and fade in the new page
      setDisplayedChildren(children)
      setCurrentPathname(locationPathname)
      setStage("fadeIn")
    }, 200) // match CSS transition duration (200ms) for smooth timing

    return () => {
      window.clearTimeout(timeout)
    }
  }, [children, locationPathname, currentPathname])

  return (
    <div
      className={`page-transition ${
        stage === "fadeIn"
          ? "page-transition--fade-in"
          : "page-transition--fade-out"
      }`}
    >
      {displayedChildren}
    </div>
  )
}

